import { useEffect, useRef, useState } from 'react'
import { useThrottleFn } from '@react-cmpt/use-throttle'
import { JSONExportPlane, JSONExportEntry, MotifGeneratorType } from '../types'
import { KaleidoscopeContainer } from './styled'
import { PositionXY } from '../../../types/PositionXY'
import { getPagePositionXY } from '../../../utils/getPagePositionXY'
import { useContextSelectedMotifs } from '../../../contexts/selectedMotifContext'
import { EXPORT_SIZE, useContextMotifGenerator } from '../../../contexts/motifGeneratorContext'
import { useContextUI } from '../../../contexts/UIContext'
import { useEnv } from '../../../utils/useEnv'
import { InfoSnackbarType } from '../../../constants/animationStates'
import { useContextScreensaver } from '../../../contexts/screensaverContext'
import { isAndroid, isIOS, isMobile } from 'react-device-detect'
import { getTransformProperties } from '../util'
import { ExportLayoutModalResponse } from '../ExportLayoutModal'

type Props = {
  type:
    | MotifGeneratorType.KALEIDO_HEXA
    | MotifGeneratorType.KALEIDO_OCTA
    | MotifGeneratorType.KALEIDO_PENTA
    | MotifGeneratorType.KALEIDO_SQUARE
  position?: PositionXY
  width?: number
  exportResponse?: ExportLayoutModalResponse
}

const TILE_NUMBER = 9
const TILE_MULTIPLIER = 3
const RESOLUTION_MULTIPLIER = window.innerWidth < 1920 ? 2 : 1
const EXPORT_RESOLUTION_MULTIPLIER = window.innerWidth < 1920 ? 2 : 1

const KaleidoscopeGeneratorCanvas = ({ type, position, width, exportResponse }: Props) => {
  const rad = Math.PI / 180
  const { isKiosk, isWeb } = useEnv()
  const { isScreensaverOn, setIsScreensaverOn } = useContextScreensaver()
  const animationPositionOffset = useRef<PositionXY>()
  const animationStartTimestamp = useRef<number>()
  const animationFrameRef = useRef<number>(0)
  const animationUnderway = useRef<boolean>(false)
  const dragging = useRef<boolean>(false)
  const canvasRefs = useRef<HTMLCanvasElement[]>([])
  const canvasToExportRef = useRef<HTMLCanvasElement>(null)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const lastPos = useRef<PositionXY>({ x: 0, y: 0 })
  const isMountedRef = useRef<boolean>(true)
  const { selectedMotifs } = useContextSelectedMotifs()
  const [canvasDataUrl, setCanvasDataUrl] = useState<string | null>(null)
  const [motifImages, setMotifImages] = useState<HTMLImageElement[]>([])
  const [patternImageIndexes, setPatternImageIndexes] = useState<number[]>([])
  const {
    exportInvoked,
    setExportInvoked,
    startExport,
    setStartExport,
    exportGeneratorCoordinatesToJSON,
    handleExport,
    handleExportCanvas,
  } = useContextMotifGenerator()
  const keepExportCanvas = useRef<HTMLCanvasElement | null>(null)
  const [imageLoadingIsFinished, setImageLoadingIsFinished] = useState<boolean>(false)
  const [imagePositionOffset, setImagePositionOffset] = useState<PositionXY>(
    position || { x: 0, y: 0 },
  )
  const { showSnackbar, hideSnackbar, disabledSnackbars, setDisabledSnackbars } = useContextUI()
  const jsonCoordinates = useRef<JSONExportPlane[]>([])
  const numberOfSlices = (() => {
    switch (type) {
      case MotifGeneratorType.KALEIDO_SQUARE:
        return 4
      case MotifGeneratorType.KALEIDO_PENTA:
        return 5
      case MotifGeneratorType.KALEIDO_HEXA:
        return 6
      case MotifGeneratorType.KALEIDO_OCTA:
        return 8
      default:
        return 2
    }
  })()
  const sliceAngle = (360 * rad) / numberOfSlices

  useEffect(() => {
    if (!startExport || !canvasDataUrl || !canvasToExportRef.current) return

    if (exportResponse === ExportLayoutModalResponse.TILES) {
      // First set the base canvas for tiling
      keepExportCanvas.current = canvasToExportRef.current
      create3By3tiles()
        .then(resolvedCanvas => {
          keepExportCanvas.current = resolvedCanvas as HTMLCanvasElement
          exportCanvas()
        })
        .catch(error => {
          console.error('Error creating tiled base64:', error)
          setExportInvoked(false)
          setCanvasDataUrl(null)
          setStartExport(false)
        })
    } else {
      keepExportCanvas.current = canvasToExportRef.current
      exportCanvas()
    }
  }, [startExport])

  const exportCanvas = async () => {
    if (!keepExportCanvas.current) return
    exportGeneratorCoordinatesToJSON({
      props: {
        type,
        selectedMotifs,
        position: imagePositionOffset,
        width: EXPORT_SIZE,
      },
      planes: jsonCoordinates.current,
      canvasSize: {
        width: canvasRefs.current[0].width,
        height: canvasRefs.current[0].height,
      },
    })
    if (isIOS || isAndroid) {
      handleExportCanvas(keepExportCanvas.current!)
    } else {
      const exportCanvasToImage = async () => {
        await handleExport(keepExportCanvas.current!)
      }
      exportCanvasToImage().catch(console.error)
    }
    setExportInvoked(false)
    setCanvasDataUrl(null)
    setStartExport(false)
  }

  const create3By3tiles = () => {
    if (!keepExportCanvas.current) {
      console.error('keepExportCanvas is not available')
      return Promise.reject(new Error('keepExportCanvas is not available'))
    }

    const tileWidth = keepExportCanvas.current.width
    const tileHeight = keepExportCanvas.current.height

    return new Promise((resolve, reject) => {
      try {
        const dataURL = keepExportCanvas.current?.toDataURL()
        if (!dataURL) {
          console.error('Failed to generate data URL from canvas')
          reject(new Error('Failed to generate data URL from canvas'))
          return
        }

        const img = new Image()
        img.crossOrigin = ''
        img.src = dataURL
        img.onload = () => {
          const tiles = document.createElement('canvas')
          tiles.width = tileWidth * 3
          tiles.height = tileHeight * 3
          tiles.className = '3x3canvas-to-export'
          const ctx = tiles.getContext('2d')

          if (!ctx) {
            reject(new Error('Failed to get canvas context'))
            return
          }

          // Pattern for kaleidoscope tiling with mirroring
          // Row 0: -x-y, x-y, -x-y
          // Row 1: -xy, xy, -xy
          // Row 2: -x-y, x-y, -x-y
          const pattern = [
            [
              { flipX: true, flipY: true },
              { flipX: false, flipY: true },
              { flipX: true, flipY: true },
            ],
            [
              { flipX: true, flipY: false },
              { flipX: false, flipY: false },
              { flipX: true, flipY: false },
            ],
            [
              { flipX: true, flipY: true },
              { flipX: false, flipY: true },
              { flipX: true, flipY: true },
            ],
          ]

          for (let y = 0; y < 3; y++) {
            for (let x = 0; x < 3; x++) {
              const { flipX, flipY } = pattern[y][x]

              ctx.save()
              ctx.translate(x * tileWidth, y * tileHeight)

              if (flipX || flipY) {
                if (flipX) ctx.translate(tileWidth, 0)
                if (flipY) ctx.translate(0, tileHeight)
                ctx.scale(flipX ? -1 : 1, flipY ? -1 : 1)
              }

              ctx.drawImage(img, 0, 0, tileWidth, tileHeight)
              ctx.restore()
            }
          }

          const canvasTiles = tiles
          tiles.remove()
          resolve(canvasTiles)
        }
        img.onerror = error => {
          console.error('Error loading image:', error)
          reject(error)
        }
      } catch (error) {
        console.error('Error in create3By3tiles:', error)
        reject(error)
      }
    })
  }

  const loadImages = (index: number) => {
    const motif = selectedMotifs[index]
    if (!motif || !motif.vectorizedUrl) {
      console.warn(`Motif at index ${index} is missing or has no vectorizedUrl`)
      return
    }

    const img = new Image()
    img.crossOrigin = ''
    img.src = motif.vectorizedUrl
    img.onload = () => {
      if (isMountedRef.current) {
        setMotifImages(prevImages => [...prevImages, img])
      }
    }
    img.onerror = error => {
      console.error(`Failed to load image at index ${index}:`, error)
    }
  }

  // Prevents pull-to refresh behavior on iOS devices
  useEffect(() => {
    if (!containerRef.current) return
    let prevent = false

    containerRef.current.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) return

      const scrollY =
        window.pageYOffset || document.body.scrollTop || document.documentElement.scrollTop
      prevent = scrollY === 0
    })

    containerRef.current.addEventListener('touchmove', function (e) {
      if (prevent) {
        prevent = false
        e.preventDefault()
      }
    })
  }, [containerRef.current])

  useEffect(() => {
    hideSnackbar()
    if (!disabledSnackbars.includes(InfoSnackbarType.KALEIDOSCOPE)) {
      if (isKiosk) showSnackbar('snackbarMessages.useKaleidoscopeKiosk')
      if (isWeb) showSnackbar('snackbarMessages.useKaleidoscopeWeb')
      if (isMobile) showSnackbar('snackbarMessages.useKaleidoscopeMobile')
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = 0
      }
      animationUnderway.current = false
      hideSnackbar()
      if (disabledSnackbars.includes(InfoSnackbarType.KALEIDOSCOPE)) {
        return
      } else {
        setDisabledSnackbars([...disabledSnackbars, InfoSnackbarType.KALEIDOSCOPE])
      }
    }
  }, [])

  useEffect(() => {
    if (motifImages.length === selectedMotifs.length) {
      const isOrdered = motifImages.every((img, idx) => {
        const motif = selectedMotifs[idx]
        return motif && motif.vectorizedUrl && img.src === motif.vectorizedUrl
      })
      if (!isOrdered) {
        const newMotifImages = motifImages.map((_, idx) => {
          const motif = selectedMotifs[idx]
          if (!motif || !motif.vectorizedUrl) {
            console.warn(`Motif at index ${idx} is missing or has no vectorizedUrl`)
            return undefined
          }
          const src = motif.vectorizedUrl
          return motifImages.find(img => img.src === src)
        })

        if (!newMotifImages.every(img => img)) {
          setMotifImages([])
          setImageLoadingIsFinished(false)
        } else {
          // SAFETY: if-else rules out undefined values
          setMotifImages(newMotifImages as HTMLImageElement[])
        }
        return
      }
      setImageLoadingIsFinished(true)

      // Always update pattern indexes based on current motif count
      // Make sure we don't use indices that exceed available motifs
      const availableMotifs = Math.min(motifImages.length, selectedMotifs.length)
      switch (availableMotifs) {
        case 1:
          setPatternImageIndexes([0, 0, 0, 0])
          break
        case 2:
          setPatternImageIndexes([0, 1, 1, 0])
          break
        case 3:
          setPatternImageIndexes([0, 1, 2, 0])
          break
        case 4:
        default:
          // For 4+ motifs, use first 4
          setPatternImageIndexes([0, 1, 2, 3])
      }
    }
    if (motifImages.length < selectedMotifs.length && !imageLoadingIsFinished) {
      loadImages(motifImages.length)
    }
    if (motifImages.length > selectedMotifs.length) {
      setMotifImages(
        motifImages.filter(img =>
          selectedMotifs.map(motif => motif.vectorizedUrl).includes(img.src),
        ),
      )
    }
  }, [selectedMotifs, motifImages, imageLoadingIsFinished])

  // Force re-render when selectedMotifs changes
  useEffect(() => {
    if (selectedMotifs.length > 0) {
      setImageLoadingIsFinished(false) // Reset loading state to trigger reload
    } else {
      // If no motifs selected, navigate back to home
      setTimeout(() => {
        window.location.href = '/'
      }, 100) // Small delay to prevent state update conflicts
    }
  }, [selectedMotifs])

  useEffect(() => {
    if (exportInvoked && canvasToExportRef.current) {
      draw(
        canvasToExportRef.current,
        EXPORT_SIZE / EXPORT_RESOLUTION_MULTIPLIER,
        EXPORT_SIZE / EXPORT_RESOLUTION_MULTIPLIER,
        imagePositionOffset,
      )
      setCanvasDataUrl(canvasToExportRef.current.toDataURL())
    }

    if (
      canvasRefs.current.length !== TILE_NUMBER ||
      motifImages.length === 0 ||
      patternImageIndexes.length !== 4 ||
      !patternImageIndexes.every(
        index => index >= 0 && index < motifImages.length && index < selectedMotifs.length,
      )
    ) {
      return
    }

    render(imagePositionOffset)

    if (intervalRef.current || canvasRefs.current.length !== TILE_NUMBER) {
      return
    }

    if (isScreensaverOn) {
      animationUnderway.current = true
      animationPositionOffset.current = imagePositionOffset
      animationFrameRef.current = requestAnimationFrame(animation)
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = 0
      }
      animationUnderway.current = false
    }
  }, [
    patternImageIndexes,
    motifImages, // Added to re-render when motifs change
    canvasRefs,
    imagePositionOffset,
    type,
    canvasToExportRef,
    exportInvoked,
    isScreensaverOn,
  ])

  useEffect(() => {
    if (!isScreensaverOn) {
      animationUnderway.current = false
    }
  }, [isScreensaverOn])

  const calculateTranslationFromTimeElapsed = (elapsed: number): PositionXY => {
    return {
      x: Math.sin((Math.PI / 12000) * elapsed) * 400,
      y: Math.cos((Math.PI / 24000) * elapsed) * 600,
    }
  }

  // Biennale Screensaver logic
  const animation = (timestamp: DOMHighResTimeStamp) => {
    if (animationStartTimestamp.current === undefined) {
      animationStartTimestamp.current = timestamp
    }

    animationPositionOffset.current = calculateTranslationFromTimeElapsed(
      timestamp - animationStartTimestamp.current,
    )

    if (animationUnderway.current) {
      animationFrameRef.current = requestAnimationFrame(animation)
    } else {
      setImagePositionOffset(animationPositionOffset.current)
      return
    }

    render(animationPositionOffset.current)
  }

  const render = (translation: PositionXY) => {
    if (canvasRefs.current[0]) {
      const clientRect = canvasRefs.current[0].getBoundingClientRect()
      draw(canvasRefs.current[0], clientRect.width, clientRect.height, translation)
      canvasRefs.current.slice(1).forEach(canvas => {
        canvas.width = clientRect.width * RESOLUTION_MULTIPLIER
        canvas.height = clientRect.height * RESOLUTION_MULTIPLIER
        const ctx = canvas.getContext('2d')
        if (ctx !== null) {
          ctx.drawImage(canvasRefs.current[0], 0, 0)
        }
      })
    }
  }

  const draw = (canvas: HTMLCanvasElement, width: number, height: number, position: PositionXY) => {
    if (!canvasRefs.current[0]) return

    if (selectedMotifs.length === 0 || motifImages.length === 0) {
      console.warn('No motifs or images available for drawing')
      return
    }
    const canvasRefBoundingRect = canvasRefs.current[0].getBoundingClientRect()
    canvas.width = width * RESOLUTION_MULTIPLIER
    canvas.height = height * RESOLUTION_MULTIPLIER
    const ctx = canvas.getContext('2d')

    if (!ctx) return

    const centerX = canvas.width / 2
    const centerY = canvas.height / 2
    const imageSize = canvas.width / 3

    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, 1000, 1000)

    const proportional = width / canvasRefBoundingRect.width
    const offset = {
      x: position.x * proportional,
      y: position.y * proportional,
    }

    let basePosX = offset.x
    let basePosY = offset.y
    let posXWithOffset = basePosX + imageSize
    let posYWithOffset = basePosY + imageSize

    let patternOffsetMultiplierX = Math.abs(Math.floor((offset.x + imageSize) / (imageSize * 2)))
    let patternOffsetMultiplierY = Math.abs(Math.floor((offset.y + imageSize) / (imageSize * 2)))

    if (offset.x > 0) {
      patternOffsetMultiplierX = -patternOffsetMultiplierX
    }

    if (offset.y > 0) {
      patternOffsetMultiplierY = -patternOffsetMultiplierY
    }

    basePosX += imageSize * 2 * patternOffsetMultiplierX
    basePosY += imageSize * 2 * patternOffsetMultiplierY
    posXWithOffset += imageSize * 2 * patternOffsetMultiplierX
    posYWithOffset += imageSize * 2 * patternOffsetMultiplierY

    const positions = {
      x: [basePosX, posXWithOffset, basePosX, posXWithOffset],
      y: [basePosY, basePosY, posYWithOffset, posYWithOffset],
    }

    jsonCoordinates.current = []

    for (let i = 0; i < numberOfSlices; i++) {
      for (let j = 0; j <= 1; j++) {
        const clipRotationAngle = sliceAngle * i + (sliceAngle / 2) * (j + 1)

        ctx.save()
        ctx.translate(centerX, centerY)
        ctx.rotate(clipRotationAngle)
        const plane = getClipPath(ctx, sliceAngle / 2, canvas.width)
        ctx.clip()
        ctx.rotate(sliceAngle / 4)
        if (j === 1) {
          ctx.scale(-1, 1)
        }
        ctx.translate(-centerX, -centerY)
        const { scale, rotation } = getTransformProperties(ctx)

        const multipliedImageOffsetPosXY = [
          { x: 2 * imageSize, y: -2 * imageSize },
          { x: 0, y: -2 * imageSize },
          { x: -2 * imageSize, y: -2 * imageSize },
          { x: -2 * imageSize, y: 0 },
          { x: 0, y: 0 },
          { x: 2 * imageSize, y: 0 },
          { x: 2 * imageSize, y: 2 * imageSize },
          { x: 0, y: 2 * imageSize },
          { x: -2 * imageSize, y: 2 * imageSize },
        ]
        const entries: JSONExportEntry[] = []
        for (let multiplierIndex = 0; multiplierIndex < 9; multiplierIndex++) {
          const currentEntries = patternImageIndexes
            .map((imageIndex, index) => {
              if (
                imageIndex >= motifImages.length ||
                imageIndex >= selectedMotifs.length ||
                imageIndex < 0
              ) {
                console.warn(
                  `Invalid image index ${imageIndex}, max available: ${
                    Math.min(motifImages.length, selectedMotifs.length) - 1
                  }`,
                )
                return null
              }

              const x = positions.x[index] + multipliedImageOffsetPosXY[multiplierIndex].x
              const y = positions.y[index] + multipliedImageOffsetPosXY[multiplierIndex].y

              const image = motifImages[imageIndex]
              const motif = selectedMotifs[imageIndex]
              if (!image || !motif) {
                console.warn(`Image or motif not found at index ${imageIndex}`)
                return null
              }

              ctx.drawImage(image, x, y, imageSize, imageSize)

              const p = ctx
                .getTransform()
                .transformPoint(new DOMPoint(x + imageSize / 2, y + imageSize / 2))
              return {
                id: motif.id,
                x: p.x,
                y: p.y,
                rotation,
                size: imageSize * scale,
                flipX: j === 1,
              }
            })
            .filter(entry => entry !== null) as JSONExportEntry[]

          entries.push(...currentEntries)
        }

        ctx.restore()

        if (plane === undefined) {
          jsonCoordinates.current.push({
            clipPlane: plane,
            entries,
          })
        } else {
          jsonCoordinates.current.push({
            clipPlane: { kind: 'polygon', points: plane },
            entries,
          })
        }
      }
    }
  }

  const getClipPath = (ctx: CanvasRenderingContext2D, angle: number, width: number) => {
    if (!canvasRefs.current[0]) return
    const cx = 0
    const cy = 0

    ctx.beginPath()
    ctx.moveTo(cx, cy)

    const triangleCoordinates = [
      { x: 0, y: 0 },
      { x: 0, y: -width },
      {
        x: -width * Math.sin(-angle),
        y: -width * Math.cos(-angle),
      },
    ]

    ctx.lineTo(triangleCoordinates[1].x, triangleCoordinates[1].y)
    ctx.lineTo(triangleCoordinates[2].x, triangleCoordinates[2].y)
    ctx.lineTo(0, 0)
    ctx.closePath()

    const t = ctx.getTransform()
    const coords: PositionXY[] = triangleCoordinates
      .map(p => t.transformPoint(new DOMPoint(p.x, p.y)))
      .map(p => Object.assign({ x: p.x, y: p.y }))
    return coords
  }

  const handleMouseDown = (event: React.MouseEvent) => {
    if (isScreensaverOn) {
      setIsScreensaverOn(false)
    } else {
      event.preventDefault()
      onStartEvent(event.pageX, event.pageY)
    }
  }

  const handleTouchStart = (event: React.TouchEvent) => {
    if (isScreensaverOn) {
      setIsScreensaverOn(false)
    } else {
      onStartEvent(event.touches[0].pageX, event.touches[0].pageY)
    }
  }

  const onStartEvent = (x: number, y: number) => {
    lastPos.current.x = x
    lastPos.current.y = y
    dragging.current = true
  }

  const handleMouseUp = (event: React.MouseEvent | React.TouchEvent) => {
    if (isScreensaverOn) {
      setIsScreensaverOn(false)
    } else {
      event.preventDefault()
      dragging.current = false
    }
  }

  const handleMouseMove = (event: React.MouseEvent | React.TouchEvent): void => {
    if (!dragging.current) return
    const pageXY = getPagePositionXY(event)
    const moveX = lastPos.current.x - pageXY.x
    const moveY = lastPos.current.y - pageXY.y

    lastPos.current = pageXY

    let offsetX = imagePositionOffset.x + moveX
    let offsetY = imagePositionOffset.y + moveY

    setImagePositionOffset({ x: offsetX, y: offsetY })
  }

  const { callback } = useThrottleFn(handleMouseMove, 50)

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      isMountedRef.current = false
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [])

  // Early return if no motifs are available to prevent rendering errors
  if (selectedMotifs.length === 0) {
    return <div>Loading...</div>
  }

  return (
    <KaleidoscopeContainer
      ref={containerRef}
      style={{
        width: width ? `${width * 3}px` : '140vw',
        height: width ? `${width * 3}px` : '140vw',
      }}
      className={`kaleidoscope-${type.toLowerCase()} ${
        isScreensaverOn ? 'screensaver-active' : ''
      }`}
      onMouseDown={handleMouseDown}
      onMouseMove={callback}
      onMouseUp={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={callback}
      onTouchEnd={handleMouseUp}
    >
      {[...Array(TILE_NUMBER)].map((_, i) => {
        return (
          <div
            key={i}
            className={'tile' + (i < 3 || i > 5 ? ' tile-flipped' : '')}
            style={{
              width: width ? `${width}px` : 'calc(100% / 3 + 1px)',
              height: width ? `${width}px` : 'calc(100% / 3 + 1px)',
            }}
          >
            <canvas ref={el => (canvasRefs.current[i] = el!)}></canvas>
          </div>
        )
      })}
      <div
        className="tile tile-to-export"
        style={{ width: `${EXPORT_SIZE}px`, height: `${EXPORT_SIZE}px` }}
      >
        <canvas
          width={EXPORT_SIZE / EXPORT_RESOLUTION_MULTIPLIER}
          height={EXPORT_SIZE / EXPORT_RESOLUTION_MULTIPLIER}
          ref={canvasToExportRef}
        ></canvas>
      </div>
    </KaleidoscopeContainer>
  )
}

export default KaleidoscopeGeneratorCanvas
